import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiService } from '../services/api';
import { User } from '../types';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  sessionKey: string | null;
  login: (usernameOrEmail: string, password: string) => Promise<{ success: boolean; message?: string }>;
  signup: (userData: {
    first_name: string;
    last_name: string;
    email: string;
    username: string;
    password: string;
    phone_number?: string;
    date_of_birth?: string;
  }) => Promise<{ success: boolean; message?: string }>;
  googleLogin: (idToken: string, accessToken?: string) => Promise<{ success: boolean; message?: string }>;
  googleLoginWithCode: (code: string, redirectUri?: string) => Promise<{ success: boolean; message?: string }>;
  logout: () => Promise<void>;
  checkAuthStatus: () => Promise<void>;
  checkAuthSync: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sessionKey, setSessionKey] = useState<string | null>(null);

  // Initialize auth state from Django's initial state
  const initializeAuthState = () => {
    try {
      const initialAuthState = (window as any).__INITIAL_AUTH_STATE__;
      if (initialAuthState) {
        console.log('Initializing auth state from Django:', initialAuthState);
        setUser(initialAuthState.user);
        setIsAuthenticated(initialAuthState.is_authenticated);
        setSessionKey(initialAuthState.session_key);
        setLoading(false);
        return true; // Successfully initialized from Django
      }
    } catch (error) {
      console.error('Error initializing auth state from Django:', error);
    }
    return false; // Failed to initialize from Django
  };

  const checkAuthStatus = async () => {
    try {
      setLoading(true);
      // Always force refresh to get current auth status (no cache)
      const response = await apiService.auth.checkStatus(true);
      setUser(response.user);
      setIsAuthenticated(response.is_authenticated);
      
      // Update session key from response if available
      if (response.is_authenticated && response.session_key) {
        setSessionKey(response.session_key);
      } else if (response.is_authenticated) {
        // Fallback: get session key via sync check if not in response
        try {
          const syncResponse = await apiService.auth.checkSync();
          if (syncResponse.session_key) {
            setSessionKey(syncResponse.session_key);
          }
        } catch (syncError) {
          console.warn('Could not get session key:', syncError);
        }
      } else {
        // Not authenticated, clear session key
        setSessionKey(null);
      }
    } catch (error) {
      console.error('Error checking auth status:', error);
      setUser(null);
      setIsAuthenticated(false);
      setSessionKey(null);
    } finally {
      setLoading(false);
    }
  };

  const checkAuthSync = async () => {
    try {
      console.log('Checking auth sync with session key:', sessionKey);
      const response = await apiService.auth.checkSync(sessionKey);
      console.log('Sync response:', response);
      
      // If session changed, user was logged out from another app
      if (response.session_changed) {
        console.log('Session changed, user logged out from another app');
        setUser(null);
        setIsAuthenticated(false);
        setSessionKey(null);
        return;
      }
      
      // Update session key if it changed (but don't create new sessions)
      if (response.session_key !== sessionKey) {
        console.log('Session key updated from', sessionKey, 'to', response.session_key);
        setSessionKey(response.session_key);
      }
      
      // Update auth state
      setUser(response.user);
      setIsAuthenticated(response.is_authenticated);
      console.log('Auth state updated - authenticated:', response.is_authenticated, 'user:', response.user?.username);
    } catch (error) {
      console.error('Error checking auth sync:', error);
      // Don't update state on error, just log it
    }
  };

  const login = async (usernameOrEmail: string, password: string) => {
    try {
      setLoading(true);
      const response = await apiService.auth.login(usernameOrEmail, password);
      setUser(response.user);
      setIsAuthenticated(response.is_authenticated);
      
      // Update session key from login response
      if (response.is_authenticated && response.session_key) {
        setSessionKey(response.session_key);
        console.log('Login successful, session key updated:', response.session_key);
      }
      
      return { success: true, message: response.message };
    } catch (error: any) {
      console.error('Login error:', error);
      const message = error.response?.data?.error || 'Login failed. Please try again.';
      return { success: false, message };
    } finally {
      setLoading(false);
    }
  };

  const signup = async (userData: {
    first_name: string;
    last_name: string;
    email: string;
    username: string;
    password: string;
    phone_number?: string;
    date_of_birth?: string;
  }) => {
    try {
      setLoading(true);
      const response = await apiService.auth.signup(userData);
      setUser(response.user);
      setIsAuthenticated(response.is_authenticated);
      
      // Update session key from signup response
      if (response.is_authenticated && response.session_key) {
        setSessionKey(response.session_key);
        console.log('Signup successful, session key updated:', response.session_key);
      }
      
      return { success: true, message: response.message };
    } catch (error: any) {
      console.error('Signup error:', error);
      const message = error.response?.data?.error || 'Signup failed. Please try again.';
      return { success: false, message };
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = async (idToken: string, accessToken?: string) => {
    try {
      setLoading(true);
      const response = await apiService.auth.googleLogin(idToken, accessToken);
      setUser(response.user);
      setIsAuthenticated(response.is_authenticated);
      
      // Update session key from login response
      if (response.is_authenticated && response.session_key) {
        setSessionKey(response.session_key);
        console.log('Google login successful, session key updated:', response.session_key);
      }
      
      return { success: true, message: response.message };
    } catch (error: any) {
      console.error('Google login error:', error);
      const message = error.response?.data?.error || 'Google login failed. Please try again.';
      return { success: false, message };
    } finally {
      setLoading(false);
    }
  };

  const googleLoginWithCode = async (code: string, redirectUri?: string) => {
    try {
      setLoading(true);
      const response = await apiService.auth.googleOAuthCallback(code, redirectUri);
      setUser(response.user);
      setIsAuthenticated(response.is_authenticated);
      
      // Update session key from login response
      if (response.is_authenticated && response.session_key) {
        setSessionKey(response.session_key);
        console.log('Google OAuth login successful, session key updated:', response.session_key);
      }
      
      return { success: true, message: response.message };
    } catch (error: any) {
      console.error('Google OAuth login error:', error);
      const message = error.response?.data?.error || 'Google login failed. Please try again.';
      return { success: false, message };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      setLoading(true);
      await apiService.auth.logout();
      setUser(null);
      setIsAuthenticated(false);
      setSessionKey(null);
      
      // Clear any user-related data from localStorage
      localStorage.removeItem('user');
      localStorage.removeItem('cart');
      localStorage.removeItem('country');
      localStorage.removeItem('currency');
      
      // Clear any cached API data
      // Note: This will force a fresh reload of data on next login
    } catch (error) {
      console.error('Logout error:', error);
      // Even if logout fails on server, clear local state
      setUser(null);
      setIsAuthenticated(false);
      setSessionKey(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Always check auth status on mount/refresh to ensure we have the latest state
    // This ensures session persistence across page refreshes
    const verifyAuth = async () => {
      try {
        setLoading(true);
        // Always get fresh auth status (no cache)
        const response = await apiService.auth.checkStatus(true);
        
        if (response.is_authenticated && response.user) {
          setUser(response.user);
          setIsAuthenticated(true);
          
          // Get session key from response or via sync check
          if (response.session_key) {
            setSessionKey(response.session_key);
          } else {
            // Fallback: get session key via sync check
            try {
              const syncResponse = await apiService.auth.checkSync();
              if (syncResponse.session_key) {
                setSessionKey(syncResponse.session_key);
              }
            } catch (syncError) {
              console.warn('Could not get session key on refresh:', syncError);
            }
          }
        } else {
          // Not authenticated
          setUser(null);
          setIsAuthenticated(false);
          setSessionKey(null);
        }
      } catch (error) {
        console.error('Error verifying auth on refresh:', error);
        // On error, try to initialize from Django state as fallback
        const initialized = initializeAuthState();
        if (!initialized) {
          setUser(null);
          setIsAuthenticated(false);
          setSessionKey(null);
        }
      } finally {
        setLoading(false);
      }
    };
    
    // Always verify auth status on mount/refresh
    verifyAuth();
  }, []);

  // Set up periodic auth sync checking
  useEffect(() => {
    if (!isAuthenticated || !sessionKey) return;

    // Check auth sync every 2 minutes when authenticated and have session
    const interval = setInterval(() => {
      console.log('Periodic sync check triggered');
      checkAuthSync();
    }, 120000); // Increased to 2 minutes to reduce unnecessary calls

    return () => clearInterval(interval);
  }, [isAuthenticated, sessionKey]);

  // Check auth sync when page becomes visible (user switched tabs)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && isAuthenticated && sessionKey) {
        checkAuthSync();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isAuthenticated, sessionKey]);

  const value: AuthContextType = {
    user,
    isAuthenticated,
    loading,
    sessionKey,
    login,
    signup,
    googleLogin,
    googleLoginWithCode,
    logout,
    checkAuthStatus,
    checkAuthSync,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
